import { colors, typography } from '@ui';
import { useTranslate } from '@i18n';

type PasswordStrengthProps = {
    password: string;
};

type Strength = 0 | 1 | 2 | 3 | 4;

const getStrength = (password: string): Strength => {
    if (!password) return 0;

    let score = 0;
    const lengthPoints = password.length >= 12 ? 2 : password.length >= 8 ? 1 : 0;
    score += lengthPoints;

    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);

    const variety = [hasLower, hasUpper, hasNumber, hasSymbol].filter(Boolean).length;
    if (variety >= 3) score += 2;
    else if (variety >= 2) score += 1;

    // Penalize obvious repeats
    const hasRepeat = /(.)\1{2,}/.test(password);
    if (hasRepeat) score = Math.max(0, score - 1);

    // Normalize to 0-4
    return Math.max(0, Math.min(4, score)) as Strength;
};

const strengthLabelKey: Record<Strength, string> = {
    0: 'user_settings.password_strength_very_weak',
    1: 'user_settings.password_strength_weak',
    2: 'user_settings.password_strength_fair',
    3: 'user_settings.password_strength_good',
    4: 'user_settings.password_strength_strong',
};

const strengthColor: Record<Strength, string> = {
    0: colors.red[500],
    1: colors.orange[500],
    2: colors.yellow[500],
    3: colors.green[500],
    4: colors.green[600],
};

export const PasswordStrength = ({ password }: PasswordStrengthProps) => {
    const score = getStrength(password);
    const t = useTranslate();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 6 }} aria-hidden>
                {[0, 1, 2, 3].map((index) => (
                    <div
                        key={index}
                        style={{
                            height: 6,
                            flex: 1,
                            borderRadius: 3,
                            backgroundColor: index <= score - 1 ? strengthColor[score] : colors.gray[200],
                            transition: 'background-color 150ms ease',
                        }}
                    />
                ))}
            </div>
            <div
                className={typography({ variant: 'h4', weight: 'bold' })}
                style={{ fontSize: 12, color: strengthColor[score] }}
                aria-live="polite"
            >
                {t(strengthLabelKey[score])}
            </div>
        </div>
    );
};
